<script setup>
import { ref, computed, onMounted, inject } from 'vue';
import { uniqBy } from 'lodash';
import recursos from '@/api/recursos';
import { match } from '../../../libs/strings';

const $I18n = inject('I18n');
const NUM_RECURSOS_FILA = 4;

const props = defineProps({
  idsRecursos: { type: Array, default: () => ([]) },
  multiple: { type: Boolean, default: true },
  cerrarAlSeleccionar: { type: Boolean, default: false },
});

const emit = defineEmits(['cancelado', 'aceptado']);

const inputFiltroNombre = ref(null);
const todosRecursos = ref([]);
const filtroNombre = ref(null);
const idCategoriaSeleccionada = ref(null);
const seleccionados = ref([]);

const recursosList = computed(() => {
  let result = todosRecursos.value;
  if (idCategoriaSeleccionada.value) {
    result = result.filter((recurso) => (
      recurso.categoria.id === idCategoriaSeleccionada.value
    ));
  }
  if (filtroNombre.value) {
    result = result.filter((recurso) => match(recurso.nombre, filtroNombre.value));
  }
  return result;
});

const categorias = computed(() => {
  const cats = todosRecursos.value
    .map((recurso) => (recurso.categoria))
    .filter((recurso) => (recurso));
  return uniqBy(cats, 'id');
});

const seleccionCategoria = (idCategoria) => {
  if (idCategoriaSeleccionada.value === idCategoria) {
    idCategoriaSeleccionada.value = null;
  } else {
    idCategoriaSeleccionada.value = idCategoria;
  }
};

const recursoSeleccionado = (recurso) => {
  if (!props.multiple) {
    seleccionados.value = recurso ? [recurso] : [];
  } else if (!seleccionados.value.includes(recurso)) {
    seleccionados.value.push(recurso);
  } else {
    seleccionados.value = seleccionados.value.filter((rec) => rec !== recurso);
  }
  if (props.cerrarAlSeleccionar) {
    aceptar();
  }
};

const aceptar = () => {
  emit('aceptado', seleccionados.value);
};

const clasesRecurso = (recurso) => {
  return {
    active: seleccionados.value.includes(recurso),
    'product-box-fixed-height': recursosList.value.length <= NUM_RECURSOS_FILA,
  };
};

onMounted(() => {
  recursos.recursos.list({ params: { reservable: true }}).then((result) => {
    todosRecursos.value = result.data;
    if (props.idsRecursos) {
      seleccionados.value = todosRecursos.value.filter((re) => props.idsRecursos.includes(re.id));
    }
  });
  inputFiltroNombre.value.focus();
});
</script>

<template>
  <div class="selector-recursos-wrapper">
    <div class="row">
      <div class="col-xs-12">
        <input
          ref="inputFiltroNombre"
          v-model="filtroNombre"
          type="text"
          class="form-control"
          :placeholder="$I18n.t('recurso')"
        />
      </div>
    </div>
    <div class="row m-t text-12x">
      <div class="col-md-12">
        <div
          v-for="categoria in categorias"
          :key="categoria.id"
          class="label-categoria label label-trans m-r-xs"
          :class="{ active: idCategoriaSeleccionada === categoria.id }"
          @click="seleccionCategoria(categoria.id)"
        >
          <i class="fas fa-circle" :style="{ color: categoria.color }"></i> {{ categoria.nombre }}
        </div>
      </div>
    </div>
    <div class="row product-grid fondo-gris m-t p-t-md">
      <div
        v-for="recurso in recursosList"
        :key="recurso.id"
        class="col-sm-3 m-b"
      >
        <div
          class="product-box-compact clickable"
          :class="clasesRecurso(recurso)"
          @click="recursoSeleccionado(recurso)"
        >
          <div class="product-box-compact-body">
            <div v-if="recurso.tipo !== 'espacio'" class="count">
              {{ recurso.existencias }}
            </div>
            <img
              class="product-box-compact-image img-responsive img-circle img-thumbnail"
              :src="recurso.foto_url"
            />
            <h3 class="m-b-xs">
              <strong>{{ recurso.nombre }}</strong>
            </h3>
            <div class="font-bold">
              {{ recurso.categoria.nombre }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="form-actions m-t-sm">
      <button class="btn btn-default" @click.prevent="$emit('cancelado')">
        {{ $I18n.t('Cancelar') }}
      </button>
      <button class="btn btn-cifra" @click.prevent="aceptar">
        {{ $I18n.t('Aceptar') }}
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @import "../../../../assets/stylesheets/cifra/vars/main.scss";

  .label-categoria {
    cursor: pointer;
    font-size: 0.8em;

    &.active {
      background-color: $cifra-color;
      color: white;
      border: none;
    }
  }

  // Si son pocos (solo una fila) se estiran para ocupar todo el alto y queda feo,
  // con esta ñapa hacemos que ocupe la mitad y quede más bonito
  .product-box-fixed-height {
    max-height: 260px;
  }
  .selector-recursos-wrapper{
    height: 100%;
    display: flex;
    flex-direction: column;

    .product-grid {
      flex-grow: 1;
      overflow: auto;
    }
  }
</style>
